import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Pencil, X, Copy, FileText, CornerDownLeft, Check, StickyNote, Sparkles, Loader2, ListChecks, CheckSquare, Square } from "lucide-react";

const STATUS = {
  idea: { label: "Idé", cls: "bg-slate-500/15 text-slate-500 border-slate-500/30" },
  todo: { label: "Skal laves", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  ready: { label: "Klar", cls: "bg-sky-500/15 text-sky-600 border-sky-500/30" },
  published: { label: "Udgivet", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
};
const STATUS_ORDER = ["idea", "todo", "ready", "published"];
const MON = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const fmtDate = (s) => { if (!s) return null; const [y, m, d] = s.split("-").map(Number); return `${d}. ${MON[m - 1]} ${y}`; };
const emptyPost = { title: "", text: "", status: "idea", date: "", note: "" };

const copy = async (text, msg = "Tekst kopieret") => {
  try { await navigator.clipboard.writeText(text || ""); toast.success(msg); }
  catch { toast.error("Kunne ikke kopiere"); }
};

export function JatakPosts() {
  const [posts, setPosts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyPost);
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [tpl, setTpl] = useState({ name: "", text: "" });
  const [tplEditId, setTplEditId] = useState(null);
  const [ai, setAi] = useState({ prompt: "", tone: "neutral", length: "mellem" });
  const [generating, setGenerating] = useState(false);
  const [todos, setTodos] = useState([]);
  const [todoText, setTodoText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, td] = await Promise.all([api.get("/jatak/posts"), api.get("/jatak/templates"), api.get("/jatak/todos")]);
      setPosts(p.data); setTemplates(t.data); setTodos(td.data);
    } catch { toast.error("Kunne ikke hente data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(emptyPost); setEditId(null); };

  const savePost = async () => {
    if (!form.title.trim()) { toast.error("Indtast en titel"); return; }
    const body = { title: form.title.trim(), text: form.text, status: form.status, date: form.date || null, note: form.note.trim() };
    try {
      if (editId) { await api.put(`/jatak/posts/${editId}`, body); toast.success("Opslag opdateret"); }
      else { await api.post("/jatak/posts", body); toast.success("Opslag gemt"); }
      resetForm(); await load();
    } catch { toast.error("Kunne ikke gemme opslag"); }
  };
  const editPost = (p) => { setEditId(p.id); setForm({ title: p.title, text: p.text, status: p.status, date: p.date || "", note: p.note || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const delPost = async (id) => {
    if (!window.confirm("Slet dette opslag?")) return;
    try { await api.delete(`/jatak/posts/${id}`); if (editId === id) resetForm(); await load(); toast.success("Opslag slettet"); }
    catch { toast.error("Kunne ikke slette"); }
  };
  const insertTemplate = (t) => { setField("text", form.text ? `${form.text}\n${t.text}` : t.text); toast.success("Skabelon indsat i teksten"); };

  const addTodo = async () => {
    const text = todoText.trim();
    if (!text) return;
    try { const { data } = await api.post("/jatak/todos", { text }); setTodos((ts) => [...ts, data]); setTodoText(""); }
    catch { toast.error("Kunne ikke tilføje"); }
  };
  const toggleTodo = async (t) => {
    setTodos((ts) => ts.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)));
    try { await api.post(`/jatak/todos/${t.id}/toggle`); }
    catch { toast.error("Kunne ikke opdatere"); setTodos((ts) => ts.map((x) => (x.id === t.id ? { ...x, done: t.done } : x))); }
  };
  const delTodo = async (id) => {
    try { await api.delete(`/jatak/todos/${id}`); setTodos((ts) => ts.filter((x) => x.id !== id)); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const generateText = async () => {
    if (!ai.prompt.trim()) { toast.error("Skriv hvad opslaget skal handle om"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/jatak/generate", ai);
      setField("text", data.text || "");
      toast.success("Tekst genereret");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke generere tekst");
    } finally { setGenerating(false); }
  };

  const saveTemplate = async () => {
    if (!tpl.name.trim()) { toast.error("Indtast et navn"); return; }
    try {
      if (tplEditId) { await api.put(`/jatak/templates/${tplEditId}`, { name: tpl.name.trim(), text: tpl.text }); toast.success("Skabelon opdateret"); }
      else { await api.post("/jatak/templates", { name: tpl.name.trim(), text: tpl.text }); toast.success("Skabelon gemt"); }
      setTpl({ name: "", text: "" }); setTplEditId(null); await load();
    } catch { toast.error("Kunne ikke gemme skabelon"); }
  };
  const editTemplate = (t) => { setTplEditId(t.id); setTpl({ name: t.name, text: t.text }); };
  const delTemplate = async (id) => {
    if (!window.confirm("Slet denne skabelon?")) return;
    try { await api.delete(`/jatak/templates/${id}`); if (tplEditId === id) { setTpl({ name: "", text: "" }); setTplEditId(null); } await load(); toast.success("Skabelon slettet"); }
    catch { toast.error("Kunne ikke slette"); }
  };

  if (loading) return <p className="text-slate-500 text-sm py-8 text-center" data-testid="jatak-loading">Henter…</p>;

  const filtered = statusFilter === "all" ? posts : posts.filter((p) => p.status === statusFilter);
  const counts = STATUS_ORDER.reduce((a, s) => ({ ...a, [s]: posts.filter((p) => p.status === s).length }), {});

  return (
    <div className="space-y-4" data-testid="jatak-stats">
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="jatak-header">
        <Megaphone className="w-4 h-4" />
        <span>Ja tak-opslag · planlæg, skriv og gem tekster · kopiér og indsæt i Ja tak / Facebook</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: posts */}
        <div className="lg:col-span-2 space-y-4">
          {/* Editor */}
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="post-editor">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">{editId ? "Redigér opslag" : "Nyt opslag"}</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Titel (fx Ugens tilbud – uge 40)" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="post-title-input" />
                <div className="flex gap-2">
                  <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                    <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 flex-1" data-testid="post-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
                      {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="h-9 w-[150px] rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="post-date-input" />
                </div>
              </div>
              <div className="rounded-xl border border-[#38BDF8]/25 bg-[#38BDF8]/5 p-3 space-y-2" data-testid="ai-generator">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-600"><Sparkles className="w-3.5 h-3.5" /> Generér tekst med AI</div>
                <Textarea value={ai.prompt} onChange={(e) => setAi((a) => ({ ...a, prompt: e.target.value }))} placeholder='Hvad skal opslaget handle om? (fx "ugens tilbud på Faxe Kondi, 10 kr")' rows={2} className="rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 resize-y" data-testid="ai-prompt-input" />
                <div className="flex flex-wrap gap-2">
                  <Select value={ai.tone} onValueChange={(v) => setAi((a) => ({ ...a, tone: v }))}>
                    <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 w-[150px]" data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
                      <SelectItem value="neutral">Tone: Neutral</SelectItem>
                      <SelectItem value="sjov">Tone: Sjov</SelectItem>
                      <SelectItem value="kort">Tone: Kort & kontant</SelectItem>
                      <SelectItem value="emojis">Tone: Med emojis</SelectItem>
                      <SelectItem value="hyggelig">Tone: Hyggelig</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={ai.length} onValueChange={(v) => setAi((a) => ({ ...a, length: v }))}>
                    <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 w-[140px]" data-testid="ai-length-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
                      <SelectItem value="kort">Længde: Kort</SelectItem>
                      <SelectItem value="mellem">Længde: Mellem</SelectItem>
                      <SelectItem value="lang">Længde: Lang</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={generateText} disabled={generating} className="h-9 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] gap-1.5" data-testid="ai-generate-btn">
                    {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Genererer…</> : <><Sparkles className="w-4 h-4" /> Generér tekst</>}
                  </Button>
                </div>
              </div>
              <Textarea value={form.text} onChange={(e) => setField("text", e.target.value)} placeholder="Skriv opslagets tekst her…" rows={5} className="rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 resize-y" data-testid="post-text-input" />
              <Input value={form.note} onChange={(e) => setField("note", e.target.value)} placeholder="Intern note (valgfri)" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="post-note-input" />
              <div className="flex gap-2 pt-1">
                <Button onClick={savePost} className="h-9 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] gap-1.5" data-testid="post-save-btn">{editId ? <><Check className="w-4 h-4" /> Gem</> : <><Plus className="w-4 h-4" /> Tilføj opslag</>}</Button>
                {editId && <Button variant="ghost" onClick={resetForm} className="h-9 rounded-lg text-slate-500 gap-1.5" data-testid="post-cancel-btn"><X className="w-4 h-4" /> Annullér</Button>}
              </div>
            </div>
          </div>

          {/* Status filter */}
          <div className="flex flex-wrap gap-2" data-testid="post-filter-bar">
            <button onClick={() => setStatusFilter("all")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${statusFilter === "all" ? "bg-[#38BDF8] text-[#0b1120] border-[#38BDF8]" : "bg-[#f8fafc] text-slate-500 border-[#e2e8f0]"}`} data-testid="post-filter-all">Alle ({posts.length})</button>
            {STATUS_ORDER.map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${statusFilter === s ? STATUS[s].cls : "bg-[#f8fafc] text-slate-500 border-[#e2e8f0]"}`} data-testid={`post-filter-${s}`}>{STATUS[s].label} ({counts[s]})</button>
            ))}
          </div>

          {/* Posts list */}
          {filtered.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center rounded-2xl bg-[#ffffff] border border-[#e2e8f0]" data-testid="post-empty">{posts.length === 0 ? "Ingen opslag endnu. Lav dit første ovenfor." : "Ingen opslag med denne status."}</p>
          ) : (
            <div className="space-y-3">
              {filtered.map((p, i) => (
                <div key={p.id} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid={`post-card-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS[p.status]?.cls}`}>{STATUS[p.status]?.label}</span>
                        {p.date && <span className="text-xs text-slate-500">{fmtDate(p.date)}</span>}
                      </div>
                      <h4 className="text-slate-800 font-medium mt-1.5">{p.title}</h4>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button className="text-slate-500 hover:text-[#38BDF8] p-1.5 rounded-lg" onClick={() => copy(p.text)} title="Kopiér tekst" data-testid={`post-copy-${i}`}><Copy className="w-4 h-4" /></button>
                      <button className="text-slate-500 hover:text-slate-900 p-1.5 rounded-lg" onClick={() => editPost(p)} title="Redigér" data-testid={`post-edit-${i}`}><Pencil className="w-4 h-4" /></button>
                      <button className="text-slate-500 hover:text-[#dc2626] p-1.5 rounded-lg" onClick={() => delPost(p.id)} title="Slet" data-testid={`post-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {p.text && <p className="text-sm text-slate-500 whitespace-pre-wrap mt-2 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-3">{p.text}</p>}
                  {p.note && <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5 flex-shrink-0" /> {p.note}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: templates */}
        <div className="space-y-3">
          {/* Tjekliste: skal bruges til Ja tak */}
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="jatak-checklist">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3 flex items-center gap-2"><ListChecks className="w-4 h-4" /> Skal bruges til Ja tak</h3>
            <div className="flex gap-2 mb-3">
              <Input value={todoText} onChange={(e) => setTodoText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTodo()} placeholder="Fx billede af tilbudsavis…" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="jatak-todo-input" />
              <Button onClick={addTodo} size="icon" className="h-9 w-9 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] flex-shrink-0" data-testid="jatak-todo-add"><Plus className="w-4 h-4" /></Button>
            </div>
            {todos.length === 0 ? (
              <p className="text-slate-400 text-xs py-3 text-center" data-testid="jatak-todo-empty">Ingen punkter endnu. Skriv hvad der skal bruges.</p>
            ) : (
              <ul className="space-y-1">
                {[...todos].sort((a, b) => (a.done === b.done ? 0 : a.done ? 1 : -1)).map((t) => (
                  <li key={t.id} className="flex items-center gap-2 group py-1" data-testid={`jatak-todo-${t.id}`}>
                    <button onClick={() => toggleTodo(t)} className="flex-shrink-0" data-testid={`jatak-todo-toggle-${t.id}`}>
                      {t.done ? <CheckSquare className="w-5 h-5 text-emerald-600" /> : <Square className="w-5 h-5 text-slate-300 hover:text-sky-400" />}
                    </button>
                    <span className={`flex-1 text-sm break-words ${t.done ? "line-through text-slate-400" : "text-slate-700"}`}>{t.text}</span>
                    <button onClick={() => delTodo(t.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-[#dc2626] flex-shrink-0" data-testid={`jatak-todo-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
            {todos.length > 0 && (
              <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-[#f1f5f9]" data-testid="jatak-todo-progress">{todos.filter((t) => t.done).length}/{todos.length} lagt op</p>
            )}
          </div>

          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="tpl-editor">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> {tplEditId ? "Redigér skabelon" : "Gemte tekster"}</h3>
            <div className="space-y-2">
              <Input value={tpl.name} onChange={(e) => setTpl((t) => ({ ...t, name: e.target.value }))} placeholder="Navn (fx Hashtags)" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="tpl-name-input" />
              <Textarea value={tpl.text} onChange={(e) => setTpl((t) => ({ ...t, text: e.target.value }))} placeholder="Tekst der kan genbruges…" rows={3} className="rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 resize-y" data-testid="tpl-text-input" />
              <div className="flex gap-2">
                <Button onClick={saveTemplate} className="h-9 rounded-lg bg-[#e2e8f0] hover:bg-[#e2e8f0] text-slate-800 gap-1.5 flex-1" data-testid="tpl-add-btn">{tplEditId ? <><Check className="w-4 h-4" /> Gem</> : <><Plus className="w-4 h-4" /> Gem tekst</>}</Button>
                {tplEditId && <Button variant="ghost" size="icon" onClick={() => { setTpl({ name: "", text: "" }); setTplEditId(null); }} className="h-9 w-9 rounded-lg text-slate-500" data-testid="tpl-cancel-btn"><X className="w-4 h-4" /></Button>}
              </div>
            </div>
          </div>

          {templates.length === 0 ? (
            <p className="text-slate-500 text-xs py-4 text-center" data-testid="tpl-empty">Ingen gemte tekster endnu.</p>
          ) : templates.map((t, i) => (
            <div key={t.id} className="rounded-xl bg-[#ffffff] border border-[#e2e8f0] p-3" data-testid={`tpl-card-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800 truncate">{t.name}</span>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button className="text-slate-500 hover:text-[#38BDF8] p-1" onClick={() => copy(t.text)} title="Kopiér" data-testid={`tpl-copy-${i}`}><Copy className="w-3.5 h-3.5" /></button>
                  <button className="text-slate-500 hover:text-emerald-600 p-1" onClick={() => insertTemplate(t)} title="Indsæt i opslag" data-testid={`tpl-insert-${i}`}><CornerDownLeft className="w-3.5 h-3.5" /></button>
                  <button className="text-slate-500 hover:text-slate-900 p-1" onClick={() => editTemplate(t)} title="Redigér" data-testid={`tpl-edit-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button className="text-slate-500 hover:text-[#dc2626] p-1" onClick={() => delTemplate(t.id)} title="Slet" data-testid={`tpl-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {t.text && <p className="text-xs text-slate-500 whitespace-pre-wrap mt-1.5 line-clamp-3">{t.text}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
